import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import {
  ArrowRight,
  Check,
  ChevronDown,
  Folder,
  FolderOpen,
  Loader2,
  Search,
  Send
} from 'lucide-react';

// Dimensions issues du modèle visuel. Les hauteurs ne se redéfinissent pas à la
// main : h-9 pour une action principale ou un pied de modal, h-8 dans une ligne
// de tableau.
const BOUTON_PIED = 'flex-1 h-9 min-w-[168px] rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
const BOUTON_PLEIN = 'bg-[#246990] text-white hover:bg-[#1f5a7a]';
const BOUTON_SECONDAIRE = 'border border-gray-200 text-gray-600 hover:bg-gray-50';
const BOUTON_DANGER = 'bg-red-600 text-white hover:bg-red-700';
const BOUTON_LIGNE = 'h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

export const PAGE_SIZE_CARDS = 6;
export const PAGE_SIZE_TABLE = 8;
export const PAGE_SIZE_AUDIT = 10;

export const usePaginatedList = (items, pageSize, resetKey = '') => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil((items?.length || 0) / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [resetKey, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return (items || []).slice(start, start + pageSize);
  }, [items, currentPage, pageSize]);

  return { currentPage, setCurrentPage, totalPages, paginatedItems, pageSize };
};

export const PaginationBar = ({
  currentPage,
  setCurrentPage,
  totalPages,
  totalItems,
  pageSize,
  label = 'element'
}) => {
  if (!totalItems || totalItems <= pageSize) return null;

  const first = ((currentPage - 1) * pageSize) + 1;
  const last = Math.min(currentPage * pageSize, totalItems);
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-lg border border-gray-200 shadow-sm p-3">
      <div className="text-xs text-gray-500">
        Affichage {first} - {last} sur {totalItems} {label}{totalItems > 1 ? 's' : ''}
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        <button
          type="button"
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className={`${BOUTON_LIGNE} ${BOUTON_SECONDAIRE}`}
        >
          &laquo;
        </button>
        <button
          type="button"
          onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
          disabled={currentPage === 1}
          className={`${BOUTON_LIGNE} ${BOUTON_SECONDAIRE}`}
        >
          Précédent
        </button>

        {pages.map((page, idx) => (
          <React.Fragment key={page}>
            {idx > 0 && pages[idx - 1] !== page - 1 && (
              <span className="px-1 text-gray-400 text-xs">...</span>
            )}
            <button
              type="button"
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                currentPage === page
                  ? 'bg-[#246990] text-white shadow-sm'
                  : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
            >
              {page}
            </button>
          </React.Fragment>
        ))}

        <button
          type="button"
          onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
          disabled={currentPage === totalPages}
          className={`${BOUTON_LIGNE} ${BOUTON_SECONDAIRE}`}
        >
          Suivant
        </button>
        <button
          type="button"
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className={`${BOUTON_LIGNE} ${BOUTON_SECONDAIRE}`}
        >
          &raquo;
        </button>
      </div>
    </div>
  );
};

const normalize = value =>
  String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = 'Sélectionner...',
  searchPlaceholder = 'Rechercher...',
  emptyLabel = 'Aucun résultat',
  disabled = false,
  getOptionValue = option => option?.value,
  getOptionLabel = option => option?.label,
  getOptionMeta = option => option?.meta,
  renderOption
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const normalizedOptions = useMemo(() => options.map((option, index) => ({
    raw: option,
    value: getOptionValue(option),
    label: getOptionLabel(option),
    meta: getOptionMeta(option),
    index
  })).filter(option => option.value !== undefined && option.value !== null), [
    options,
    getOptionValue,
    getOptionLabel,
    getOptionMeta
  ]);

  const selected = normalizedOptions.find(option => String(option.value) === String(value));
  const filtered = normalizedOptions.filter(option =>
    normalize(`${option.label || ''} ${option.meta || ''}`).includes(normalize(query))
  );

  const selectOption = option => {
    onChange(option.value, option.raw);
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(value => !value)}
        className={`w-full min-h-[38px] px-3 py-2 rounded-lg border text-xs transition-all flex items-center justify-between gap-3 ${
          disabled
            ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-white border-gray-200 text-gray-700 hover:border-[#246990]/40 focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]'
        }`}
      >
        <span className={`truncate ${selected ? 'font-semibold text-gray-800' : 'text-gray-400'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-[80] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
              <input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className="w-full h-8 pl-8 pr-3 rounded-md bg-gray-50 border border-gray-100 text-xs focus:outline-none focus:ring-2 focus:ring-[#246990]/20 focus:border-[#246990]"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-400 text-center">{emptyLabel}</div>
            ) : filtered.map(option => {
              const isSelected = String(option.value) === String(value);
              return (
                <button
                  key={`${option.value}-${option.index}`}
                  type="button"
                  onClick={() => selectOption(option)}
                  className={`w-full px-3 py-2.5 text-left text-xs transition-colors flex items-center justify-between gap-3 ${
                    isSelected ? 'bg-[#246990]/10 text-[#246990]' : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {renderOption ? renderOption(option.raw, isSelected) : (
                    <span className="min-w-0">
                      <span className="block font-semibold truncate">{option.label}</span>
                      {option.meta && <span className="block text-[11px] text-gray-400 truncate">{option.meta}</span>}
                    </span>
                  )}
                  {isSelected && <Check size={13} className="text-[#246990] flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const TransferExecutionOverlay = ({
  title = 'Transfert en cours',
  subtitle = 'Le dossier est en cours de bascule vers le site cible.',
  name,
  source,
  target,
  status = 'Synchronisation des données...'
}) => (
  // Écran de progression bloquant : un Dialog, mais qui refuse Échap et le clic
  // extérieur. On n'interrompt pas un transfert en cours d'un coup de touche.
  <Dialog open>
    <DialogContent
      className="w-[95vw] max-w-lg p-0 gap-0 overflow-hidden"
      onEscapeKeyDown={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
      onPointerDownOutside={(e) => e.preventDefault()}
    >
      <DialogTitle className="sr-only">{title}</DialogTitle>
      <motion.div
        initial={{ y: 16, scale: 0.96, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
      >
      <div className="px-6 py-5 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          </div>
          <div className="w-9 h-9 rounded-lg bg-[#246990]/10 flex items-center justify-center">
            <Loader2 size={18} className="text-[#246990] animate-spin" />
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="w-24 text-center">
            <motion.div
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              className="w-16 h-16 mx-auto rounded-lg bg-gray-900 text-white flex items-center justify-center shadow-sm"
            >
              <FolderOpen size={30} />
            </motion.div>
            <p className="mt-2 text-[11px] font-semibold text-gray-800 truncate">{source || 'Site source'}</p>
            <p className="text-[11px] text-gray-400">Départ</p>
          </div>

          <div className="flex-1 min-w-[120px]">
            <div className="relative h-16 flex items-center">
              <div className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-[#246990]/15" />
              <motion.div
                className="absolute top-1/2 -translate-y-1/2"
                animate={{ left: ['0%', '82%'] }}
                transition={{ duration: 1.15, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="w-8 h-8 rounded-full bg-white border border-[#246990]/20 shadow-md flex items-center justify-center">
                  <Send size={14} className="text-[#246990]" />
                </div>
              </motion.div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[#246990] text-white flex items-center justify-center shadow-sm">
                <ArrowRight size={15} />
              </div>
            </div>
          </div>

          <div className="w-24 text-center">
            <motion.div
              animate={{ y: [0, 3, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              className="w-16 h-16 mx-auto rounded-lg bg-amber-400 text-white flex items-center justify-center shadow-sm"
            >
              <Folder size={30} />
            </motion.div>
            <p className="mt-2 text-[11px] font-semibold text-gray-800 truncate">{target || 'Site cible'}</p>
            <p className="text-[11px] text-gray-400">Arrivée</p>
          </div>
        </div>

        <div className="mt-5 p-3 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-xs font-semibold text-gray-900 truncate">{name || 'Dossier inter-site'}</p>
          <div className="mt-2 flex items-center gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-gray-200 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-[#246990]"
                animate={{ x: ['-100%', '110%'] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
            <span className="text-[11px] font-semibold text-[#246990] whitespace-nowrap">{status}</span>
          </div>
        </div>
      </div>
      </motion.div>
    </DialogContent>
  </Dialog>
);
